import { getProductCalConfig } from '../../calEventMappings';
import { getUserById } from '../../data/users';
import { getProductById } from '../../data/products';
import { getDirectorById } from '../../data/directors';
import { getPersonalBrandManagerById } from '../../data/personalBrandManagers';
import { logger } from '@/utils/logger';

/**
 * Centralizes all Cal.com slug selection logic
 * 
 * FIRST-TIMER :
 *   → Shooting: no director_id assigned
 *   → Preparation/Editorial: no personal_brand_manager_id assigned
 *   → Uses round-robin slugs from cal_event_mappings
 *   → first_timer_shooting_slug from cal_event_mappings table
 *   → first_timer_preparation_slug from cal_event_mappings table
 * 
 * RECURRING :
 *   → Shooting: director_id is assigned
 *   → Preparation/Editorial: personal_brand_manager_id is assigned
 *   → Uses event_manage slugs from cal_event_mappings
 *   → Shooting : managed_shooting_slug from cal_event_mappings + director.cal_username
 *   → Preparation : managed_preparation_slug from cal_event_mappings + pbm.cal_username
 *   → URL format: {cal_username}/{slug}
 */
export async function getCalSlugForBooking(
  productId: string,
  eventType: 'shooting' | 'preparation',
  userId: string,
  locationId?: string
): Promise<{
  eventTypeSlug: string;
  teamSlug?: string;
  username?: string;
  eventTypeId?: number;
} | null> {
  try {
    const userResult = await getUserById(userId);
    
    if (!userResult.success || !userResult.data) {
      console.error(`❌ User not found for userId=${userId}`);
      return null;
    }
    
    const user = userResult.data;
    
    const config = await getProductCalConfig(productId, locationId);
    
    if (!config) {
      console.warn(`⚠️ No Cal.com config found for productId=${productId}, locationId=${locationId}`);
      return null;
    }
    
    const isFirstTimer = eventType === 'shooting' 
      ? !user.director_id
      : !user.personal_brand_manager_id;
    
    if (isFirstTimer) {
      return await getFirstTimerSlug(config, eventType);
    }
    
    return await getRecurringSlug(config, productId, eventType, user);
    
  } catch (error) {
    console.error('❌ Error in getCalSlugForBooking:', error);
    return null;
  }
}

async function getFirstTimerSlug(
  config: any,
  eventType: 'shooting' | 'preparation'
): Promise<{
  eventTypeSlug: string;
  teamSlug: string;
  eventTypeId?: number;
  username?: string;
} | null> {
  let eventSlug: string | null = null;
  let eventId: number | null = null;
  
  if (eventType === 'shooting') {
    eventSlug = config.first_timer_shooting_slug;
    eventId = config.first_timer_shooting_id || null;
  } else {
    eventSlug = config.first_timer_preparation_slug;
    eventId = config.first_timer_preparation_id || null;
  }
  
  if (!eventSlug) {
    console.warn(`⚠️ No first-timer slug found for eventType=${eventType}`);
    return null;
  }
  
  const eventIdStr = eventId ? ` (ID: ${eventId})` : '';
  logger.debug(`✅ First-timer booking: using ${eventSlug}${eventIdStr} (round-robin)`);
  
  return {
    eventTypeSlug: eventSlug,
    teamSlug: config.cal_team_slug,
    username: undefined,
    eventTypeId: eventId || undefined,
  };
}

async function getRecurringSlug(
  config: any,
  productId: string,
  eventType: 'shooting' | 'preparation',
  user: any
): Promise<{
  eventTypeSlug: string;
  username?: string;
  teamSlug?: string;
  eventTypeId?: number;
} | null> {
  const product = await getProductById(productId);
  
  if (!product) {
    console.error(`❌ Product not found: ${productId}`);
    return null;
  }
  
  if (eventType === 'shooting') {
    return await getRecurringShootingSlug(config, product, user);
  } else {
    return await getRecurringPreparationSlug(config, product, user);
  }
}

async function getRecurringShootingSlug(
  config: any,
  product: any,
  user: any
): Promise<{
  eventTypeSlug: string;
  username?: string;
  teamSlug?: string;
  eventTypeId?: number;
} | null> {
  const eventSlug = config.managed_shooting_slug;
  const eventId = config.managed_shooting_id || null;
  
  if (!eventSlug) {
    console.error(`❌ No managed_shooting_slug slug found in cal_event_mappings for productId=${product.id}`);
    return null;
  }
  
  if (!user.director_id) {
    console.error(`❌ No director assigned to user ${user.id} for recurring shooting`);
    return null;
  }
  
  const directorResult = await getDirectorById(user.director_id);
  
  if (!directorResult.success || !directorResult.data) {
    console.error(`❌ Director not found: ${user.director_id}`);
    return null;
  }
  
  const director = directorResult.data;
  const calUsername = director.cal_username || null;

  if (!calUsername) {
    console.error(`❌ No cal_username found for director ${director.first_name} ${director.last_name} (user_id: ${director.user_id})`);
    return null;
  }

  return {
    eventTypeSlug: eventSlug,
    username: calUsername,
    teamSlug: config.cal_team_slug,
    eventTypeId: eventId || undefined,
  };
}

async function getRecurringPreparationSlug(
  config: any,
  product: any,
  user: any
): Promise<{
  eventTypeSlug: string;
  username?: string;
  teamSlug?: string;
  eventTypeId?: number;
} | null> {
  const eventSlug = config.managed_preparation_slug;
  const eventId = config.managed_preparation_id || null;
  
  if (!eventSlug) {
    console.error(`❌ No managed_preparation_slug slug found in cal_event_mappings for productId=${product.id}`);
    return null;
  }
  
  if (!user.personal_brand_manager_id) {
    console.error(`❌ No PBM assigned to user ${user.id} for recurring preparation`);
    return null;
  }
  
  const pbmResult = await getPersonalBrandManagerById(user.personal_brand_manager_id);
  
  if (!pbmResult.success || !pbmResult.data) {
    console.error(`❌ PBM not found: ${user.personal_brand_manager_id}`);
    return null;
  }
  
  const pbm = pbmResult.data;
  const calUsername = pbm.cal_username || null;
  
  if (!calUsername) {
    console.error(`❌ No cal_username found for PBM ${pbm.first_name} ${pbm.last_name} (user_id: ${pbm.user_id})`);
    return null;
  }
  
  const eventIdStr = eventId ? ` (ID: ${eventId})` : '';
  logger.debug(`✅ Recurring preparation: using ${calUsername}/${eventSlug}${eventIdStr} from cal_event_mappings`);
  
  return {
    eventTypeSlug: eventSlug,
    username: calUsername,
    teamSlug: config.cal_team_slug,
    eventTypeId: eventId || undefined,
  };
}

